import React from 'react';
import { Link } from 'react-router-dom';
import { Typography, Box } from '@mui/material';
import './LandingPage.css';
import { Footer } from '../layout';
import { analytics } from '../../firebaseConfig';

import { logEvent } from 'firebase/analytics';
import Seo from '../common/Seo';

const LandingPage = () => {
  const [activeStep, setActiveStep] = React.useState(0);

  const handleCtaClick = (ctaType) => {
    logEvent(analytics, 'landing_cta_clicked', { cta_type: ctaType });
  };

  React.useEffect(() => {
    let cancelled = false;

    const sequence = async () => {
      while (!cancelled) {
        setActiveStep(0);
        await new Promise(r => setTimeout(r, 1000));
        if (cancelled) break;

        setActiveStep(1);
        await new Promise(r => setTimeout(r, 800));
        if (cancelled) break;

        setActiveStep(2);
        await new Promise(r => setTimeout(r, 800));
        if (cancelled) break;

        setActiveStep(3);
        await new Promise(r => setTimeout(r, 800));
        if (cancelled) break;

        setActiveStep(4);
        await new Promise(r => setTimeout(r, 4000));
      }
    };

    sequence();

    return () => { cancelled = true; };
  }, []);

  const getGateStatus = (gateIndex) => {
    if (activeStep > gateIndex + 1) return 'passed';
    if (activeStep === gateIndex + 1) return 'validating';
    return 'pending';
  };

  return (
    <div className="ntb-landing-container">
      <Seo
        title="NanoTradingCopilot - Validate Before You Trade"
        description="Stop second-guessing your forex trades. NanoTradingCopilot validates your trade ideas against configurable rules before you execute. Manage risk, check R:R, and align with trends automatically."
        keywords="forex trading, trade validation, risk management, trading psychology, forex tools, trading discipline"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "NanoTradingCopilot",
          "applicationCategory": "FinanceApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "description": "Stop second-guessing your forex trades. Validate your trade ideas against configurable rules before you execute."
        }}
      />
      {/* Hero Section */}
      <section className="ntb-hero">
        <div className="ntb-hero-content">
          <Typography variant="h1" className="ntb-hero-title">
            Validate Before You Trade
          </Typography>
          <Typography variant="body1" className="ntb-hero-subtitle">
            Your trading rules, enforced automatically. Get clear pass/fail gate results in seconds — so you never skip your checklist again.
          </Typography>
          <Box className="ntb-hero-cta">
            <Link to="/signup" className="ntb-btn ntb-btn-primary" onClick={() => handleCtaClick('hero_signup')}>
              Get Early Access
            </Link>
            <Link to="/signup" className="ntb-btn ntb-btn-ghost" onClick={() => handleCtaClick('hero_signup_free')}>
              Sign Up Free
            </Link>
          </Box>
        </div>
        <div className="ntb-hero-visual">
          <div className="ntb-validation-preview">
            <div className="ntb-preview-header">
              <span className="ntb-preview-dot"></span>
              <span className="ntb-preview-dot"></span>
              <span className="ntb-preview-dot"></span>
            </div>
            <div className="ntb-preview-content">
              <div className="ntb-preview-trade">
                <span className="ntb-preview-label">EUR/USD</span>
                <span className="ntb-preview-direction ntb-long">LONG</span>
              </div>
              <div className="ntb-preview-gates">
                <div className={`ntb-preview-gate ntb-gate-${getGateStatus(0)}`}>
                  <span className="ntb-gate-icon">
                    {getGateStatus(0) === 'passed' ? '\u2713' : getGateStatus(0) === 'validating' ? '\u21BB' : '\u2022'}
                  </span>
                  <span>Risk: 1.2%</span>
                </div>
                <div className={`ntb-preview-gate ntb-gate-${getGateStatus(1)}`}>
                  <span className="ntb-gate-icon">
                    {getGateStatus(1) === 'passed' ? '\u2713' : getGateStatus(1) === 'validating' ? '\u21BB' : '\u2022'}
                  </span>
                  <span>R:R: 2.5:1</span>
                </div>
                <div className={`ntb-preview-gate ntb-gate-${getGateStatus(2)}`}>
                  <span className="ntb-gate-icon">
                    {getGateStatus(2) === 'passed' ? '\u2713' : getGateStatus(2) === 'validating' ? '\u21BB' : '\u2022'}
                  </span>
                  <span>Trend aligned</span>
                </div>
              </div>
              <div className={`ntb-preview-result ${activeStep === 4 ? 'visible' : ''} ntb-result-passed`}>
                PASSED
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="ntb-social-proof">
        <div className="ntb-proof-items">
          <div className="ntb-proof-item">
            <span className="ntb-proof-value">15</span>
            <span className="ntb-proof-label">Validation Rules</span>
          </div>
          <div className="ntb-proof-divider"></div>
          <div className="ntb-proof-item">
            <span className="ntb-proof-value">Real-time</span>
            <span className="ntb-proof-label">Market Data</span>
          </div>
          <div className="ntb-proof-divider"></div>
          <div className="ntb-proof-item">
            <span className="ntb-proof-value">&lt;3s</span>
            <span className="ntb-proof-label">Validation Time</span>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="ntb-section ntb-how-it-works">
        <Typography variant="h2" className="ntb-section-title">
          How It Works
        </Typography>
        <div className="ntb-steps">
          <div className="ntb-step">
            <div className="ntb-step-num">01</div>
            <Typography variant="h4" className="ntb-step-title">Enter Trade</Typography>
            <Typography variant="body2" className="ntb-step-desc">
              Input your entry, stop loss, and target — or let AI extract them from your chat
            </Typography>
          </div>
          <div className="ntb-step">
            <div className="ntb-step-num">02</div>
            <Typography variant="h4" className="ntb-step-title">Validate</Typography>
            <Typography variant="body2" className="ntb-step-desc">
              Your rules check risk, R:R, trend, timing, and market conditions in seconds
            </Typography>
          </div>
          <div className="ntb-step">
            <div className="ntb-step-num">03</div>
            <Typography variant="h4" className="ntb-step-title">Decide</Typography>
            <Typography variant="body2" className="ntb-step-desc">
              Get a clear GO or NO-GO result with detailed reasoning for each rule
            </Typography>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="ntb-section ntb-features">
        <Typography variant="h2" className="ntb-section-title">
          Built for Discipline
        </Typography>
        <div className="ntb-features-grid">
          <div className="ntb-feature">
            <div className="ntb-feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <Typography variant="h5" className="ntb-feature-title">Risk Management</Typography>
            <Typography variant="body2" className="ntb-feature-desc">
              Enforce max risk per trade, open risk limits, and drawdown protection automatically
            </Typography>
          </div>
          <div className="ntb-feature">
            <div className="ntb-feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
            </div>
            <Typography variant="h5" className="ntb-feature-title">Technical Alignment</Typography>
            <Typography variant="body2" className="ntb-feature-desc">
              AI checks trend direction, momentum, and key level proximity across timeframes
            </Typography>
          </div>
          <div className="ntb-feature">
            <div className="ntb-feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <Typography variant="h5" className="ntb-feature-title">Market Timing</Typography>
            <Typography variant="body2" className="ntb-feature-desc">
              Block trades during low-liquidity windows and before high-impact news events
            </Typography>
          </div>
          <div className="ntb-feature">
            <div className="ntb-feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </div>
            <Typography variant="h5" className="ntb-feature-title">Trading Journal</Typography>
            <Typography variant="body2" className="ntb-feature-desc">
              Log trades with PnL calendar, equity curve, and performance analytics
            </Typography>
          </div>
          <div className="ntb-feature">
            <div className="ntb-feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="9" y1="9" x2="15" y2="15" />
                <line x1="15" y1="9" x2="9" y2="15" />
              </svg>
            </div>
            <Typography variant="h5" className="ntb-feature-title">Configurable Rules</Typography>
            <Typography variant="body2" className="ntb-feature-desc">
              Customize thresholds or create AI-powered custom rules to match your strategy
            </Typography>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="ntb-section ntb-cta-section">
        <Typography variant="h2" className="ntb-cta-title">
          Stop Guessing. Start Validating.
        </Typography>
        <Typography variant="body1" className="ntb-cta-subtitle">
          Join traders who validate before they execute. Free to start, no credit card required.
        </Typography>
        <Link to="/signup" className="ntb-btn ntb-btn-primary ntb-btn-lg" onClick={() => handleCtaClick('footer_signup')}>
          Get Early Access
        </Link>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
